import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { StyleConfigInterface } from './platforms/web/src/config/themeConfig/stylesInterface';
import { getStyleConfig, globalStyles } from './platforms/web/src/config/themeConfig/styles';
import { getAppConfig } from './platforms/web/src/config/appConfig';
import { brandingTheme } from './platforms/web/src/config/themeConfig/branding';
import LoadingOverlay from './packages/ui-react/src/components/LoadingOverlay/LoadingOverlay';
import { useConfigStore } from './packages/common/src/stores/ConfigStore';

interface ThemeContextType {
  universalStyles: StyleConfigInterface;
  setUniversalStyles: React.Dispatch<React.SetStateAction<StyleConfigInterface>>;
  themeConfig: any;
  setThemeConfig: React.Dispatch<React.SetStateAction<any>>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const defaultThemeContext: ThemeContextType = {
  universalStyles: globalStyles,
  setUniversalStyles: () => undefined,
  themeConfig: brandingTheme,
  setThemeConfig: () => undefined,
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      return defaultThemeContext;
    }
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [universalStyles, setUniversalStyles] = useState<StyleConfigInterface>(globalStyles);
  const [themeConfig, setThemeConfig] = useState<any>(brandingTheme);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const config = await getAppConfig();
        if (config) {
          setThemeConfig(config?.theme?.theme_colors);
          setUniversalStyles(
            getStyleConfig(config?.theme?.theme_colors, {
              primary_font: config?.appConfig?.branding?.primary_font,
              secondary_font: config?.appConfig?.branding?.secondary_font,
            }),
          );
          updateRootStyles(config?.theme?.theme_colors, config?.appConfig?.branding?.primary_font);
          useConfigStore.setState((s) => ({
            ...s,
            appConfig: config.appConfig,
            featureFlags: config.theme,
            analyticsConfig: config.analyticsConfig,
          }));
        }
      } catch (error) {
        console.error('Error loading local app config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => { }, []);

  const updateRootStyles = (config: any, fontConfig: string) => {
    const root = document.documentElement;
    root.style.setProperty('--body-background-color', config?.background);
    root.style.setProperty('--highlight-color', config?.primary);
    root.style.setProperty('--primary-btn-color', config?.primary);
    root.style.setProperty('--primary-color', config?.neutral_white);
    root.style.setProperty('--neutral-white', config?.neutral_white);
    root.style.setProperty('--card-color', config?.neutral_white);
    root.style.setProperty('--body-font-family', fontConfig);
    root.style.setProperty('--modal-background', config?.neutral_900);
    root.style.setProperty('--secondary-btn-color', config?.neutral_900);
    root.style.setProperty('--blue', config?.neutral_900);
    root.style.setProperty('--neutral-black', config?.neutral_black);
    root.style.setProperty('--neutral-500', config?.neutral_500);
    root.style.setProperty('--neutral-400', config?.neutral_400);
    root.style.setProperty('--neutral-300', config?.neutral_300);
    root.style.setProperty('--neutral-alpha-10', config?.neutral_alpha_10);
    root.style.setProperty('--neutral-alpha-20', config?.neutral_alpha_20);
    root.style.setProperty('--neutral-alpha-30', config?.neutral_alpha_30);
    root.style.setProperty('--neutral-alpha-40', config?.neutral_alpha_40);
    root.style.setProperty('--neutral-alpha-55', config?.neutral_alpha_55);
    root.style.setProperty('--neutral-alpha-80', config?.neutral_alpha_80);
    root.style.setProperty('--neutral-800', config?.neutral_800);
    root.style.setProperty('--neutral-700', config?.neutral_700);
    root.style.setProperty('--neutral-600', config?.neutral_600);
    root.style.setProperty('--button-text-color', config?.button_text);
    root.style.setProperty('--primary-gradient-color', config?.primary_gradient);
    root.style.setProperty('--primary-border-color', config?.primary_border);
    root.style.setProperty('--alert-error', config?.alert_error);
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return <ThemeContext.Provider value={{ universalStyles, setUniversalStyles, themeConfig, setThemeConfig }}>{children}</ThemeContext.Provider>;
};
